/* ============================================================
   HOD header search — README (design_handoff_search) behaviours the theme's SearchBar lacks:
     · data-hod-filled on .search-bar while the input has text (shows the ✕ between input and button)
     · --hod-srch-left on the card so the 880px results card never runs past the viewport
     · real result count: "Top N of M" + "View all M results for “q” ›" via templates/search.hod-count.liquid
     · keyboard: ↑/↓ move a highlighted row (screen order), Enter follows it ONLY when the card matches the
       typed query, Tab moves focus to it; first suggestion pre-highlighted; combobox / listbox aria wiring
   Theme.js still owns open/close, fetching and rendering (.search-bar__results-inner); custom.js owns Esc/outside-click.
     · 9/28: product-type refine — a second section request with q="product_type:<type> AND <query>" when the card says data-hod-type.
   ============================================================ */
(function () {
  'use strict';
  var root = document.querySelector('.header__search-bar-wrapper');
  if (!root) return;
  var bar = root.querySelector('.search-bar');
  var input = root.querySelector('.search-bar__input');
  var inner = root.querySelector('.search-bar__inner');
  var resultsInner = root.querySelector('.search-bar__results-inner');
  var menuWrap = root.querySelector('.search-bar__menu-wrapper');
  if (!bar || !input || !inner) return;
  function qsa(el, sel) { return Array.prototype.slice.call(el.querySelectorAll(sel)); }

  /* ---- filled state (theme.js empties the field itself on pageshow / clear) ---- */
  function syncFilled() { bar.setAttribute('data-hod-filled', input.value ? 'true' : 'false'); }
  input.addEventListener('input', function () { syncFilled(); setActive(null); });   /* typing invalidates any highlight */
  input.addEventListener('focus', syncFilled);
  window.addEventListener('pageshow', syncFilled);
  root.addEventListener('click', function (e) {
    if (e.target.closest('[data-action="clear-input"]')) setTimeout(function () { syncFilled(); setActive(null); input.focus(); }, 0);
  });
  syncFilled();

  /* ---- 9/29 (Stas): one tap opens the phone search AND puts the cursor in the field, so the keyboard
     comes up at once (was: tap the icon, then tap the field). Bubble phase on document: theme.js's
     section-level toggle has already run (it added .is-visible when this tap OPENS the bar), and
     custom.js's X handler (capture, stopPropagation while .is-fixed) never lets a close reach here —
     so the order is exactly the old two-tap one: toggle, then focus. The wrapper is still
     visibility:hidden (the theme fades it in over .25s) and a hidden field cannot take focus, so
     both are forced visible for the tap; iOS only shows the keyboard for a focus() made inside the
     tap gesture, hence no timers before it. The inline values clear after the fade (clearing on the
     next frame re-hid the field at transition p = 0 and WebKit blurred it). Verified WebKit 375 9/29. ---- */
  document.addEventListener('click', function (e) {
    if (!e.target.closest('[data-action="toggle-search"]')) return;
    if (!root.classList.contains('is-visible') || root.classList.contains('is-fixed')) return;   /* this tap closed it, or it is already open */
    /* 9/30 (Stas): keep the theme's open animation (the bar slides down .25s and fades in) — the field takes focus
       at the tap (iOS needs that for the keyboard) while .is-opening keeps the suggestions panel out of sight until
       the slide has ended; then the panel is pinned under the settled field and fades in. */
    /* 9/30 later (Stas): the list should open TOGETHER with the bar, not after it — so the panel is pinned at the
       bar's settled position up front (the theme's slide moves the bar down by 59px) and fades in over the same
       window the bar takes to slide and fade (.is-opening animation in hod-search.css); the settled position is
       re-measured once the slide has ended as a safety. */
    root.style.visibility = 'visible';
    input.style.visibility = 'visible';
    var settledTop = null;   /* where the bar's bottom edge will be once the slide ends: measured AFTER custom.js's
                                observer (it scrolls the page to 0 — the header is not sticky, so a tap from a
                                scrolled page would otherwise pin the panel too high; review 9/30), before the
                                slide's first frame, so bottom + 59px */
    var pin = function () { if (settledTop === null) settledTop = (Math.ceil(bar.getBoundingClientRect().bottom) + 59) + 'px'; document.documentElement.style.setProperty('--hod-msearch-top', settledTop); };
    root.classList.add('is-opening');
    try { input.focus({ preventScroll: true }); } catch (err) { input.focus(); }
    /* custom.js re-pins the panel from a MutationObserver (queued by the focus above) with the mid-slide position —
       queue after it so the settled position wins, and once more on the next frame */
    Promise.resolve().then(pin); requestAnimationFrame(pin);
    var opened = false;
    /* the slide's end re-pins the panel; the .is-opening class only leaves once the panel's own fade has finished
       (dropping it early snapped the fade to 1 while the bar was still at .6 — Stas 9/30) */
    var onEnd = function (e) { if (e.target === root && e.propertyName === 'margin-bottom') { root.removeEventListener('transitionend', onEnd); fixPanelTop(); } };
    root.addEventListener('transitionend', onEnd);
    var reveal = function () { if (opened) return; opened = true; inner.removeEventListener('animationend', reveal); root.classList.remove('is-opening'); };
    inner.addEventListener('animationend', reveal);
    setTimeout(reveal, 600);   /* fallback if the animation never fires (reduced motion) */
    setTimeout(function () { root.style.visibility = ''; input.style.visibility = ''; fixPanelTop(); }, 500);
    setTimeout(fixPanelTop, 900);
  });
  /* custom.js positions the fixed suggestions panel (Popular searches / results) at --hod-msearch-top =
     the field's bottom edge, measured the moment .is-fixed lands. With one tap that moment is while the
     bar is still sliding down (the theme's margin-bottom .25s transition), so the panel was pinned ~59px
     too high and covered the field — Stas's "the popular items pop up but the box is hidden" (9/29).
     Re-measure once the slide has settled. */
  function fixPanelTop() {
    if (!root.classList.contains('is-fixed')) return;
    document.documentElement.style.setProperty('--hod-msearch-top', Math.ceil(bar.getBoundingClientRect().bottom) + 'px');
  }
  root.addEventListener('transitionend', function (e) { if (e.propertyName === 'margin-bottom') fixPanelTop(); });

  /* ---- card left offset → CSS var (hod-search.css caps the 880px card at 100vw − 24px − left) ---- */
  function syncLeft() { inner.style.setProperty('--hod-srch-left', Math.round(bar.getBoundingClientRect().left) + 'px'); }
  syncLeft();
  window.addEventListener('resize', syncLeft);

  /* ---- a11y wiring: combobox → listbox ---- */
  inner.id = inner.id || 'hod-search-listbox';
  inner.setAttribute('role', 'listbox');
  input.setAttribute('role', 'combobox');
  input.setAttribute('aria-controls', inner.id);
  input.setAttribute('aria-autocomplete', 'list');
  input.setAttribute('aria-haspopup', 'listbox');
  function syncExpanded() {
    var open = bar.classList.contains('is-expanded');
    input.setAttribute('aria-expanded', open ? 'true' : 'false');
    if (open) syncLeft();
  }
  new MutationObserver(syncExpanded).observe(bar, { attributes: true, attributeFilter: ['class'] });
  syncExpanded();

  /* ---- rows ---- */
  var ROW = '.search-bar__menu-link, .search-bar__result-link, .search-bar__result-item, .search-bar__inner > .search-bar__view-all';
  function isOption(el) { return !!el && !el.classList.contains('search-bar__view-all'); }
  function byScreenOrder(a, b) { var ra = a.getBoundingClientRect(), rb = b.getBoundingClientRect(); return (ra.top - rb.top) || (ra.left - rb.left); }
  function visibleRows() {
    return qsa(inner, ROW).filter(function (el) { return !el.closest('[aria-hidden="true"]') && el.offsetParent !== null; }).sort(byScreenOrder);
  }
  var seq = 0;
  function setActive(el) {
    qsa(inner, '.is-active').forEach(function (o) { o.classList.remove('is-active'); o.removeAttribute('aria-selected'); });
    input.removeAttribute('aria-activedescendant');
    if (!el) return;
    el.classList.add('is-active');
    if (isOption(el)) {
      if (!el.id) el.id = 'hod-ps-opt-' + (++seq);
      el.setAttribute('aria-selected', 'true');
      input.setAttribute('aria-activedescendant', el.id);
    }
    if (typeof el.scrollIntoView === 'function') el.scrollIntoView({ block: 'nearest' });
  }
  function activeRow() { return inner.querySelector('.is-active'); }
  function currentCard() { return resultsInner ? resultsInner.querySelector('[data-hod-ps]') : null; }
  /* the results card is only trustworthy when it was rendered for what is in the field right now */
  function cardIsCurrent() {
    var ps = currentCard();
    if (!ps || bar.classList.contains('is-loading')) return false;
    return (ps.getAttribute('data-q') || '').trim().toLowerCase() === input.value.trim().toLowerCase();
  }

  input.addEventListener('keydown', function (e) {
    var k = e.key;
    var cur = activeRow();
    if (k === 'Tab') {
      /* keep DOM focus and the highlight together (theme.js would otherwise focus the FIRST link) */
      if (cur && !e.shiftKey) { e.preventDefault(); e.stopPropagation(); cur.focus(); }
      return;
    }
    if (k !== 'ArrowDown' && k !== 'ArrowUp' && k !== 'Enter') return;
    var rows = visibleRows();
    if (!rows.length) return;
    var i = rows.indexOf(cur);
    if (k === 'Enter') {
      if (!cur || i === -1 || !isOption(cur)) return;                                  /* nothing chosen → the form submits the typed text */
      if (resultsInner && resultsInner.contains(cur) && !cardIsCurrent()) return;      /* stale card → the form submits the typed text */
      e.preventDefault();
      if (cur.tagName === 'A' && cur.href) window.location.href = cur.href; else cur.click();
      return;
    }
    e.preventDefault();
    if (k === 'ArrowDown') i = i < rows.length - 1 ? i + 1 : 0; else i = i > 0 ? i - 1 : rows.length - 1;
    setActive(rows[i]);
  });
  inner.addEventListener('mousemove', function (e) {
    var row = e.target.closest(ROW);
    if (row && !row.classList.contains('is-active')) setActive(row);
  });
  inner.addEventListener('focusin', function (e) {
    var row = e.target.closest(ROW);
    if (row) setActive(row);
  });

  /* ---- results rendered → pre-highlight the first SUGGESTION (never a collection / page / product) + real count ---- */
  var countCache = Object.create(null); var ctrl = null; var timer = null;
  function footerFor(q) {
    var f = inner.querySelector('[data-hod-ps-viewall]');
    return f && f.getAttribute('data-q') === q ? f : null;
  }
  function applyCount(q, m) {
    var ps = currentCard();
    if (!ps || ps.getAttribute('data-q') !== q) return;
    var c = ps.querySelector('[data-hod-ps-count]');
    var shown = c ? (c.getAttribute('data-shown') || '3') : '3';
    var foot = footerFor(q);
    if (m === 0) {                                   /* only pages / articles matched: no product results page to offer */
      if (c) c.textContent = 'Top ' + shown;
      if (foot) foot.hidden = true;
      return;
    }
    var label = m == null ? null : (m >= 1000 ? '1,000+' : m.toLocaleString());   /* Shopify caps search.results_count at 1000 */
    if (c) c.textContent = 'Top ' + shown + (label ? ' of ' + label : '');
    if (foot) {
      foot.hidden = false;
      var t = foot.querySelector('[data-hod-ps-viewall-text]');
      if (t) t.textContent = label ? 'View all ' + label + ' result' + (m === 1 ? '' : 's') + ' for “' + q + '”' : 'View all results for “' + q + '”';
    }
  }
  function fetchCount(q) {
    if (Object.prototype.hasOwnProperty.call(countCache, q)) { applyCount(q, countCache[q]); return; }
    if (ctrl) ctrl.abort();
    ctrl = ('AbortController' in window) ? new AbortController() : null;
    var url = (window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/') + 'search?view=hod-count&type=product&options%5Bprefix%5D=last&q=' + encodeURIComponent(q);
    fetch(url, { credentials: 'same-origin', signal: ctrl ? ctrl.signal : undefined })
      .then(function (r) { return r.ok ? r.text() : ''; })
      .then(function (t) { var m = parseInt(String(t).trim(), 10); if (!isNaN(m)) { countCache[q] = m; applyCount(q, m); } })
      .catch(function () {});
  }
  /* ---- bold the typed words inside product titles + collection / builder names (Stas 9/6; Revision: first occurrence for collections) ---- */
  function escRe(str) { return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'); }
  function boldIn(el, re, tag, cls) {
    tag = tag || 'b'; cls = cls || 'hod-ps__hit';
    var walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT, null);
    var nodes = []; var n;
    while ((n = walker.nextNode())) { if (n.nodeValue.trim() && !n.parentNode.closest('.' + cls + ', mark')) nodes.push(n); }
    var hit = false;
    nodes.forEach(function (node) {
      var text = node.nodeValue; re.lastIndex = 0;
      if (!re.test(text)) return;
      re.lastIndex = 0;
      var frag = document.createDocumentFragment(); var last = 0; var m;
      while ((m = re.exec(text))) {
        if (!m[0]) { re.lastIndex++; continue; }
        if (m.index > last) frag.appendChild(document.createTextNode(text.slice(last, m.index)));
        var b = document.createElement(tag); if (cls) b.className = cls; b.textContent = m[0]; frag.appendChild(b);
        last = m.index + m[0].length; hit = true;
        if (!re.global) break;
      }
      if (last < text.length) frag.appendChild(document.createTextNode(text.slice(last)));
      node.parentNode.replaceChild(frag, node);
    });
    return hit;
  }
  function boldProducts(scope, q) {
    var words = q.split(/\s+/).filter(function (w) { return w.length >= 2; });
    if (!words.length) return;
    var reAll = new RegExp('(' + words.map(escRe).join('|') + ')', 'gi');
    qsa(scope, '[data-hod-ps-title], .search-bar__item-sku').forEach(function (t) { boldIn(t, reAll); });
  }
  function boldMatches(ps, q) {
    var words = q.split(/\s+/).filter(function (w) { return w.length >= 2; });
    if (!words.length) return;
    var reAll = new RegExp(words.map(escRe).join('|'), 'gi');
    qsa(ps, '[data-hod-ps-title], .search-bar__item-sku').forEach(function (t) { boldIn(t, reAll); });   /* products: every typed word, title + SKU */
    var reQ = new RegExp(escRe(q.trim()), 'i');
    qsa(ps, '.search-bar__result-link--collection .hod-ps__txt').forEach(function (t) {  /* collections: whole query first, else each word */
      if (!boldIn(t, reQ)) boldIn(t, reAll);
    });
    /* suggestions (Stas 9/6): the typed words are never bold — Shopify only <mark>s a leading match, so a suggestion like
       "zline kn 36 conversion kit" came back all bold. Re-mark every typed word ourselves; the rest stays bold. */
    qsa(ps, '.hod-ps__left .search-bar__result-link:not(.search-bar__result-link--collection):not(.search-bar__result-link--plain):not(.search-bar__result-link--builder) .hod-ps__txt').forEach(function (t) {
      qsa(t, 'mark').forEach(function (mk) { mk.replaceWith(document.createTextNode(mk.textContent)); });
      t.normalize();
      boldIn(t, reAll, 'mark', '');
    });
  }

  /* 9/28 — product-type refine: Shopify's section rendering matches the typed word against product TYPES too, so "range" hands the
     popup only range hoods. When the section decided the query means one product type (data-hod-type, set only when an exact
     category pin leads), ask Shopify once more with q="product_type:<type> AND <query>" and swap the card. The refined card carries
     no data-hod-type (its terms contain " AND "), so this never loops; a query typed meanwhile wins (checked before and after). */
  function sameQuery(q) { return input.value.replace(/\s+/g, ' ').trim().toLowerCase() === q.toLowerCase(); }
  function refineByType(ps, q) {
    var type = ps.getAttribute('data-hod-type');
    if (!type || !q || !resultsInner || !sameQuery(q)) return;
    var rootPath = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    var url = rootPath + 'search/suggest?section_id=predictive-search&q=' + encodeURIComponent('product_type:' + type + ' AND ' + q) +
      '&resources[limit]=10&resources[limit_scope]=each&resources[options][fields]=title,product_type,variants.title,variants.sku,vendor';
    fetch(url, { credentials: 'same-origin' })
      .then(function (r) { return r.ok ? r.text() : ''; })
      .then(function (t) {
        if (!t || !sameQuery(q)) return;
        var sec = new DOMParser().parseFromString(t, 'text/html').querySelector('.search-ajax');
        var cur = currentCard();
        if (!sec || !cur || (cur.getAttribute('data-q') || '') !== q) return;   /* results moved on meanwhile */
        var newRight = sec.querySelector('.hod-ps__right'), oldRight = cur.querySelector('.hod-ps__right');
        cur.removeAttribute('data-hod-type');                                    /* refined once, never again for this card */
        if (!newRight) return;                                                    /* the type filter found nothing: keep Shopify's list */
        if (oldRight) oldRight.replaceWith(newRight); else cur.appendChild(newRight);
        boldProducts(newRight, q);                                                /* only the products column changes; suggestions + collections stay */
        var m = countCache[q]; if (typeof m === 'number') applyCount(q, m);
      })
      .catch(function () {});
  }

  function onRendered() {
    var ps = currentCard();
    if (!ps) return;
    if (!ps.hasAttribute('data-hod-bolded')) { ps.setAttribute('data-hod-bolded', '1'); boldMatches(ps, ps.getAttribute('data-q') || ''); }
    setActive(ps.querySelector('.hod-ps__left .search-bar__result-link:not(.search-bar__result-link--collection):not(.search-bar__result-link--plain):not(.search-bar__result-link--builder)'));
    var q = ps.getAttribute('data-q') || '';
    clearTimeout(timer);
    if (q) timer = setTimeout(function () { fetchCount(q); }, 120);
    refineByType(ps, q);
  }
  if (resultsInner) new MutationObserver(function () { onRendered(); }).observe(resultsInner, { childList: true });
  if (menuWrap) new MutationObserver(function () { if (menuWrap.getAttribute('aria-hidden') === 'false') setActive(null); }).observe(menuWrap, { attributes: true, attributeFilter: ['aria-hidden'] });
})();
